<script setup lang="ts">
import { debounce } from "perfect-debounce";
import SimpleDropdown from "~/components/Common/Dropdowns/SimpleDropdown.vue";
import { ATTENDANCE_USER_GROUP_LABELS, filterParticipants } from "~/utils/helpers/attendance/participants";
import { useAttendanceReviewStore } from "~/store/attendance/attendance-review.store";
import { AttendanceUserGroupEnum } from "~/utils/enums/attendance-review.enums";
import { DebounceEnum } from "~/utils/enums/debounce.enums";
import type {
  IAttendanceParticipantGroup,
  IAttendanceParticipantOption,
  IAttendanceSignatureRow,
} from "~/utils/interfaces/attendance-review-interfaces";

const props = withDefaults(defineProps<{ row: IAttendanceSignatureRow; warning?: boolean }>(), { warning: false });

const emit = defineEmits<{ pick: [IAttendanceParticipantOption | null] }>();

const store = useAttendanceReviewStore();
const search = ref("");

/** Everyone tied to another line — this row's own pick stays offered, it is the selected value. */
const pairedElsewhere = computed(() => {
  const paired = new Set(store.pairedUserIds);
  if (props.row.userId) paired.delete(props.row.userId);

  return paired;
});

/**
 * The two groups, in the order the design lists them. Empty ones are dropped rather than passed
 * through: vue-multiselect prints a header for every group it is given, and "Inscrits à cette
 * session" with nothing under it reads as "nobody is booked on this session".
 */
const groups = computed<IAttendanceParticipantGroup[]>(() => {
  // Booked, minus whoever another line already declares — filtered here rather than server-side so
  // pairing a row updates the open list at once, and a failed refresh cannot empty the group.
  const booked = store.sessionUsers.filter((user) => !pairedElsewhere.value.has(user.userId));
  const sessionUsers = filterParticipants(booked, search.value);
  // Whoever the first group already offers must not be repeated by the second one.
  const listed = new Set([...pairedElsewhere.value, ...sessionUsers.map((user) => user.userId)]);

  return [
    { label: ATTENDANCE_USER_GROUP_LABELS[AttendanceUserGroupEnum.SESSION], users: sessionUsers },
    {
      label: ATTENDANCE_USER_GROUP_LABELS[AttendanceUserGroupEnum.COMPANY],
      users: store.companyUsers.filter((user) => !listed.has(user.userId)),
    },
  ].filter((group) => group.users.length > 0);
});

const selected = computed(() =>
  props.row.userId ? { userId: props.row.userId, displayValue: props.row.displayValue ?? "" } : null,
);

const searchCompanyUsers = debounce((query: string) => store.searchCompanyUsers(query), DebounceEnum.HIGH);

/**
 * This row's own spinner. The lists live in the store, shared by every line of the table, so
 * showing the store's loading flag lit up all the other pickers at once — one dropdown paging made
 * the whole column look busy. Only the picker that asked for a page reports it.
 */
const isFetching = ref(false);

async function fetching(load: Promise<unknown>) {
  isFetching.value = true;
  try {
    await load;
  } finally {
    isFetching.value = false;
  }
}

function onOpen() {
  search.value = "";
  void fetching(Promise.all([store.loadSessionUsers(), store.searchCompanyUsers()]));
}

function onSearch(query: string) {
  // The session group is filtered here and now; the company group has to go back to the API for it.
  search.value = query;
  void fetching(searchCompanyUsers(query));
}

/** Scrolled to the end of the list: only "Tous les utilisateurs" has more to give, the roster is whole. */
function onLoadMore() {
  void fetching(store.loadMoreCompanyUsers());
}
</script>

<template>
  <div :class="{ picker__warning: warning }">
    <SimpleDropdown
      :model-value="selected"
      :options="groups"
      group-values="users"
      group-label="label"
      label="displayValue"
      track-by="userId"
      placeholder="Sélectionner un participant..."
      :searchable="true"
      :internal-search="false"
      :busy="isFetching"
      :delete="true"
      :open-fetch="true"
      :nb_Element="7"
      @open="onOpen()"
      @search-value="onSearch"
      @load-more="onLoadMore"
      @update:model-value="emit('pick', $event)"
    >
      <template #singleLabel="{ option }">
        <span>{{ option.displayValue }}</span>
      </template>
      <!-- Group headers travel through the same slot as the options, flagged with `$isLabel`. -->
      <template #option="{ option }">
        <span v-if="option.$isLabel" class="meta text-tertiary-color">{{ option.$groupLabel }}</span>
        <span v-else class="body_regular">{{ option.displayValue }}</span>
      </template>
    </SimpleDropdown>
  </div>
</template>

<style lang="scss" scoped>
/*
 * vue-multiselect's own control: it is two components down, so `:deep` is the only way in. The
 * design flags a doubtful identity in amber, where the shared `error` prop only offers red.
 */
.picker__warning :deep(.multiselect__tags) {
  border-color: var(--orange-main);
}
</style>
